import React from 'react';
import { Link } from 'react-router-dom';
import {
    Shield,
    CheckCircle2,
    ArrowRight,
    ArrowLeft,
    MousePointer2,
    Eye,
    Lock,
    Sparkles
} from 'lucide-react';

const BetaAccess = () => {
    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 font-sans selection:bg-indigo-500/30">

            {/* --- Navigation --- */}
            <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <img src="/logo.png" alt="Subby Logo" className="h-9 w-auto object-contain -mr-2" />
                        <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700 tracking-tight">Subby</span>
                    </Link>
                    <Link to="/" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-1">
                        <ArrowLeft className="w-4 h-4" />
                        Back to Home
                    </Link>
                </div>
            </nav>

            {/* --- Main Content --- */}
            <main className="pt-32 pb-20 px-6">
                <div className="max-w-2xl mx-auto">

                    {/* Header */}
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-50 border border-indigo-100 text-sm text-indigo-600 font-semibold mb-6 shadow-sm">
                            <Sparkles className="w-4 h-4" />
                            Beta Access
                        </div>
                        <h1 className="text-4xl md:text-5xl font-extrabold mb-4 tracking-tight text-gray-900 leading-tight">
                            Get Early Access to Subby
                        </h1>
                        <p className="text-lg text-gray-600 max-w-xl mx-auto leading-relaxed">
                            Subby is fully functional right now. We're just completing Google's standard security certification (CASA). Here's what to expect.
                        </p>
                    </div>

                    {/* What to expect */}
                    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-8">
                        <div className="px-6 py-5 border-b border-gray-100 bg-gray-50">
                            <h2 className="text-xl font-bold text-gray-900">What You'll See During Signup</h2>
                            <p className="text-sm text-gray-500 mt-1">A standard Google verification notice — not a security issue</p>
                        </div>

                        <div className="p-6 space-y-6">

                            {/* Step 1 */}
                            <div className="flex gap-4">
                                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                                    <span className="text-lg font-bold text-indigo-600">1</span>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-1">Click "Sign up with Google"</h3>
                                    <p className="text-gray-600 text-sm">Start the signup process as normal. Google will show its standard sign-in screen.</p>
                                </div>
                            </div>

                            {/* Step 2 */}
                            <div className="flex gap-4">
                                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                                    <span className="text-lg font-bold text-amber-600">2</span>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-1">You'll see a warning screen</h3>
                                    <p className="text-gray-600 text-sm mb-3">
                                        Google shows this for all apps that haven't completed their verification yet. It says <strong>"Google hasn't verified this app"</strong> — this is normal for beta apps.
                                    </p>
                                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                                        <div className="flex items-start gap-3">
                                            <Eye className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                            <div className="text-sm text-amber-800">
                                                <p className="font-medium mb-1">Click "Advanced" at the bottom left</p>
                                                <p>Then click <strong>"Go to subby.io (unsafe)"</strong> to proceed.</p>
                                                <p className="mt-2 text-amber-600">Don't worry — it's not actually unsafe. Google just labels all unverified apps this way during the certification process.</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Step 3 */}
                            <div className="flex gap-4">
                                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                                    <span className="text-lg font-bold text-green-600">3</span>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-1">Grant Gmail access</h3>
                                    <p className="text-gray-600 text-sm">
                                        Make sure to check the box that says <strong>"View your email messages and settings"</strong>. This lets Subby scan your inbox for subscription receipts.
                                    </p>
                                </div>
                            </div>

                            {/* Step 4 */}
                            <div className="flex gap-4">
                                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                                    <span className="text-lg font-bold text-indigo-600">4</span>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-1">You're in!</h3>
                                    <p className="text-gray-600 text-sm">Complete the quick onboarding and Subby will automatically find your subscriptions.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Trust signals */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                            <Lock className="w-6 h-6 text-indigo-600 mx-auto mb-2" />
                            <p className="text-sm font-medium text-gray-900">Read-only access</p>
                            <p className="text-xs text-gray-500 mt-1">Can never edit, send, or delete emails</p>
                        </div>
                        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                            <Shield className="w-6 h-6 text-green-600 mx-auto mb-2" />
                            <p className="text-sm font-medium text-gray-900">End-to-end encrypted</p>
                            <p className="text-xs text-gray-500 mt-1">Your data is never shared or sold</p>
                        </div>
                        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                            <MousePointer2 className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                            <p className="text-sm font-medium text-gray-900">CASA pending</p>
                            <p className="text-xs text-gray-500 mt-1">Google's security audit is in progress</p>
                        </div>
                    </div>

                    {/* CTA */}
                    <div className="text-center space-y-4">
                        <Link
                            to="/signup"
                            className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-indigo-600 text-white font-semibold text-lg hover:bg-indigo-700 transition-all duration-300 hover:shadow-xl hover:shadow-indigo-500/20 active:scale-95 group"
                        >
                            Continue to Signup
                            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                        </Link>

                        <p className="text-sm text-gray-500 flex items-center justify-center gap-1.5">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            Free tier available • No credit card required
                        </p>

                        <p className="text-sm text-gray-400 mt-6">
                            Not ready yet? <Link to="/" className="text-indigo-600 font-medium hover:text-indigo-700">Join the waitlist instead</Link>
                        </p>
                    </div>

                </div>
            </main>

            {/* --- Footer --- */}
            <footer className="py-12 px-6 border-t border-gray-200 bg-white">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-2">
                        <img src="/logo.png" alt="Subby Logo" className="h-7 w-auto object-contain -mr-1" />
                        <span className="font-bold text-gray-900 tracking-tight">Subby</span>
                        <span className="text-gray-400 text-sm ml-2">© {new Date().getFullYear()} Subby. All rights reserved.</span>
                    </div>
                    <div className="flex flex-wrap justify-center gap-8 text-sm font-medium text-gray-500">
                        <Link to="/terms" className="hover:text-indigo-600 transition-colors">Terms of Use</Link>
                        <Link to="/privacy" className="hover:text-indigo-600 transition-colors">Privacy Policy</Link>
                        <a href="mailto:support@subby.io" className="hover:text-indigo-600 transition-colors">support@subby.io</a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default BetaAccess;
